import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  SpinnerGap,
  CheckCircle,
  XCircle,
  Phone,
  Envelope,
  MapPin,
  Receipt,
  CreditCard,
  Calendar,
  Warning
} from "phosphor-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { loadFinixScript, initializeFinixForm } from "../lib/finixFlex";
import { getFinixFraudSessionId } from "../utils/finixFraudSession";
// Removed: PoweredByFSPCard import - branding footer not shown on payment pages

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicInvoice() {
  const { publicToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  
  // Payment state
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [finixReady, setFinixReady] = useState(false);
  const finixFormRef = useRef(null);
  const fraudSessionIdRef = useRef(null);

  useEffect(() => {
    fetchInvoice();
  }, [publicToken]);

  const fetchInvoice = async () => {
    try {
      const response = await fetch(`${API}/public/invoice/${publicToken}`);
      
      if (!response.ok) {
        const data = await response.json();
        setError(data.detail || "Unable to load invoice");
        setLoading(false);
        return;
      }
      
      const data = await response.json();
      setInvoice(data);
    } catch (err) {
      setError("Unable to load invoice. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Initialize Finix PaymentForm when dialog opens
  useEffect(() => {
    if (!showPayDialog || !paymentConfig?.ok) return;
    
    let mounted = true;
    let initAttempted = false;
    
    const initFinix = async () => {
      if (initAttempted) return;
      initAttempted = true;
      
      try {
        await loadFinixScript();
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (!mounted) return;
        
        const isDebug = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
        
        // Start fraud session collection in parallel (best-effort, non-blocking)
        // Uses application_id or merchant_id from paymentConfig
        const env = paymentConfig.environment;
        const appId = paymentConfig.application_id;
        const merchId = paymentConfig.merchant_id;
        
        // Per Finix docs: merchant_id is REQUIRED for fraud detection
        // Only attempt fraud session if we have merchantId
        if (merchId) {
          if (isDebug) {
            console.info('[FSP_FRAUD_DEBUG] Starting fraud session collection:', {
              env,
              merchId: merchId ? `***${merchId.slice(-4)}` : null
            });
          }
          getFinixFraudSessionId({
            environment: env,
            merchantId: merchId,
            timeoutMs: 3000
          }).then(sessionId => {
            if (mounted) {
              fraudSessionIdRef.current = sessionId;
              if (isDebug) {
                console.info('[FSP_FRAUD_DEBUG] Fraud session stored:', { length: sessionId?.length || 0 });
              }
            }
          });
        } else if (isDebug) {
          console.info('[FSP_FRAUD_DEBUG] No merchantId available - skipping fraud session');
        }
        
        const container = document.getElementById("public-invoice-finix-form");
        if (!container) {
          console.error("[PublicInvoice] Container not found");
          return;
        }
        
        container.innerHTML = "";
        
        const form = initializeFinixForm("public-invoice-finix-form", {
          environment: paymentConfig.environment,
          applicationId: paymentConfig.application_id,
          onTokenize: async (data) => {
            const isDebugSubmit = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
            
            if (isDebugSubmit) {
              console.info('[FSP_FRAUD_DEBUG] onTokenize called, token present:', !!data?.tokenId);
            }
            
            if (!mounted) return;
            
            const tokenId = data?.tokenId;
            const TOKEN_REGEX = /^TK[a-zA-Z0-9]{10,}$/;
            
            if (!tokenId) {
              console.error("[PublicInvoice] No token received from Finix");
              toast.error("Card processing failed. Please try again.");
              return;
            }
            
            if (!TOKEN_REGEX.test(tokenId)) {
              console.error("[PublicInvoice] Invalid token format:", tokenId?.substring(0, 10));
              toast.error("Invalid card token. Please try again.");
              return;
            }
            
            setProcessingPayment(true);
            
            try {
              // Include fraud_session_id if available (best-effort)
              // If not ready yet, wait up to 300ms for it (fail-open)
              let fraudSessionId = fraudSessionIdRef.current;
              
              if (isDebugSubmit) {
                console.info('[FSP_FRAUD_DEBUG] Pre-submit fraud session from ref:', { length: fraudSessionId?.length || 0 });
              }
              
              // Per Finix docs: merchant_id is required for fraud detection
              if (!fraudSessionId && paymentConfig.merchant_id) {
                // Quick wait for fraud session if still pending
                try {
                  fraudSessionId = await Promise.race([
                    getFinixFraudSessionId({
                      environment: paymentConfig.environment,
                      merchantId: paymentConfig.merchant_id,
                      timeoutMs: 300
                    }),
                    new Promise(resolve => setTimeout(() => resolve(null), 300))
                  ]);
                } catch {
                  // Fail-open: proceed without fraud session
                }
              }
              
              const payload = { token: tokenId };
              if (fraudSessionId && typeof fraudSessionId === "string" && fraudSessionId.trim() !== "") {
                payload.fraud_session_id = fraudSessionId;
              }
              
              if (isDebugSubmit) {
                console.info('[FSP_FRAUD_DEBUG] Final payload keys:', Object.keys(payload));
              }
              
              const response = await fetch(`${API}/public/invoice/${publicToken}/pay`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
              });
              
              let result;
              try {
                result = await response.json();
              } catch (parseErr) {
                console.error("Failed to parse response:", parseErr);
                toast.error("Invalid server response. Please try again.");
                setProcessingPayment(false);
                return;
              }
              
              if (!response.ok || !result?.success) {
                const errorMsg = result?.error || result?.detail || result?.message || "Payment failed";
                console.error("[PublicInvoice] Payment failed:", errorMsg, result?.diag);
                toast.error(errorMsg);
                setProcessingPayment(false);
                return;
              }
              
              toast.success("Payment successful!");
              setShowPayDialog(false);
              setProcessingPayment(false);
              
              // Re-fetch invoice to show updated status
              await fetchInvoice();
              
            } catch (err) {
              console.error("Payment processing error:", err);
              toast.error("Payment failed. Please try again.");
              setProcessingPayment(false);
            }
          },
          onError: (error) => {
            console.error("[PublicInvoice] Tokenization error:", error);
            if (mounted) {
              toast.error(error?.message || "Card validation failed");
              setProcessingPayment(false);
            }
          },
          onUpdate: (state, binInfo, hasErrors) => {
            // Form state updated
          }
        });
        
        if (form && mounted) {
          finixFormRef.current = form;
          setFinixReady(true);
        }
      } catch (err) {
        console.error("Error initializing payment form:", err);
        if (mounted) {
          toast.error("Failed to load payment form. Please refresh and try again.");
        }
      }
    };
    
    initFinix();
    
    return () => {
      mounted = false;
      finixFormRef.current = null;
      setFinixReady(false);
    };
  }, [showPayDialog, paymentConfig, publicToken]);
  
  // Open Pay dialog
  const handleOpenPay = () => {
    const isDebug = localStorage.getItem('FSP_FRAUD_DEBUG') === '1';
    
    if (!invoice?.payments_enabled || !invoice?.finix_application_id) {
      toast.error("Online payments are not available for this invoice");
      return;
    }
    
    // Robust key selection: try multiple possible key names
    const env = invoice.finix_environment || invoice.environment || "sandbox";
    const applicationId = invoice.finix_application_id || invoice.application_id || null;
    const merchantId = invoice.finix_merchant_id || invoice.merchant_id || null;
    
    if (isDebug) {
      console.info('[FSP_FRAUD_DEBUG] Invoice payment config keys:', {
        hasFinixEnv: !!invoice.finix_environment,
        hasEnv: !!invoice.environment,
        hasFinixAppId: !!invoice.finix_application_id,
        hasAppId: !!invoice.application_id,
        hasMerchantId: !!invoice.finix_merchant_id || !!invoice.merchant_id,
        chosenEnv: env,
        chosenAppId: applicationId ? `***${applicationId.slice(-4)}` : null,
        chosenMerchantId: merchantId ? `***${merchantId.slice(-4)}` : null
      });
    }
    
    const config = {
      ok: true,
      provider: "finix",
      application_id: applicationId,
      environment: env,
      merchant_id: merchantId,
      payment_amount: invoice.balance_due,
      payment_amount_cents: Math.round(invoice.balance_due * 100)
    };
    
    setPaymentConfig(config);
    setShowPayDialog(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return "—";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="public-invoice-loading">
        <div className="text-center">
          <SpinnerGap className="w-10 h-10 animate-spin text-emerald-600 mx-auto" />
          <p className="text-zinc-500 mt-4">Loading invoice...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4" data-testid="public-invoice-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-zinc-900 mb-2">Unable to Load Invoice</h1>
            <p className="text-zinc-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const company = invoice?.company || {};
  const isPaid = invoice?.status === "paid";
  const canPay = invoice?.can_pay && invoice?.payments_enabled;

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="public-invoice-page">
      {/* Header with company branding */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {company.logo_url ? (
                <div className="relative">
                  <img 
                    src={company.logo_url} 
                    alt={company.name} 
                    className="h-10 max-w-[150px] object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <span className="text-lg font-semibold text-zinc-900 hidden">
                    {company.name}
                  </span>
                </div>
              ) : (
                <span className="text-lg font-semibold text-zinc-900">{company.name}</span>
              )}
            </div>
            {isPaid && (
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle className="w-4 h-4 mr-1" />
                Paid
              </Badge>
            )}
            {invoice?.status === "partial" && (
              <Badge className="bg-amber-100 text-amber-700">
                <Warning className="w-4 h-4 mr-1" />
                Partial Payment
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">
        {/* Invoice header */}
        <Card className="mb-4 overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <p className="text-sm text-zinc-500 mb-1">Invoice</p>
                <h1 className="text-2xl font-bold text-zinc-900">
                  #{invoice?.invoice_number}
                </h1>
                {invoice?.title && (
                  <p className="text-zinc-600 mt-1">{invoice.title}</p>
                )}
              </div>
              <div className="text-left sm:text-right">
                <p className="text-sm text-zinc-500">Created</p>
                <p className="font-medium">{formatDate(invoice?.created_at)}</p>
                {invoice?.due_date && (
                  <p className="text-sm text-zinc-500 mt-2 flex items-center sm:justify-end gap-1">
                    <Calendar className="w-4 h-4" />
                    Due: {formatDate(invoice.due_date)}
                  </p>
                )}
              </div>
            </div>
            
            {invoice?.customer_name && (
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <p className="text-sm text-zinc-500">Bill To</p>
                <p className="font-medium text-zinc-900">{invoice.customer_name}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Paid banner */}
        {isPaid && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4 flex items-center gap-3" data-testid="invoice-paid-banner">
            <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-emerald-800">Invoice Paid</p>
              <p className="text-sm text-emerald-600">
                Thank you for your payment
              </p>
            </div>
          </div>
        )}

        {/* Payment required banner */}
        {canPay && !isPaid && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4" data-testid="payment-required-banner">
            <div className="flex items-start gap-3">
              <CreditCard className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">Payment Due</p>
                <p className="text-sm text-amber-700 mt-1">
                  Balance due: <span className="font-semibold">{formatCurrency(invoice.balance_due)}</span>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Line items */}
        <Card className="mb-4">
          <CardContent className="p-0">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="font-semibold text-zinc-900 flex items-center gap-2">
                <Receipt className="w-5 h-5 text-zinc-400" />
                Line Items
              </h2>
            </div>
            
            <div className="divide-y divide-zinc-100">
              {invoice?.items?.map((item, idx) => (
                <div key={idx} className="p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-zinc-900">{item.description}</p>
                      <p className="text-sm text-zinc-500 mt-1">
                        {item.quantity} × {formatCurrency(item.price)}
                        {item.is_taxable && <span className="ml-2 text-xs text-zinc-400">(taxable)</span>}
                      </p>
                    </div>
                    <p className="font-medium text-zinc-900 whitespace-nowrap">
                      {formatCurrency(item.quantity * item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="mb-4">
          <CardContent className="p-4 space-y-3">
            <div className="flex justify-between text-zinc-600">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice?.subtotal)}</span>
            </div>
            {invoice?.tax > 0 && (
              <div className="flex justify-between text-zinc-600">
                <span>Tax ({invoice?.tax_rate_percent}%)</span>
                <span>{formatCurrency(invoice?.tax)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold text-zinc-900 pt-3 border-t border-zinc-200">
              <span>Total</span>
              <span>{formatCurrency(invoice?.total)}</span>
            </div>
            {invoice?.amount_paid > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Amount Paid</span>
                <span>-{formatCurrency(invoice?.amount_paid)}</span>
              </div>
            )}
            {invoice?.balance_due > 0 && (
              <div className="flex justify-between text-lg font-bold text-amber-700 pt-2 border-t border-amber-200">
                <span>Balance Due</span>
                <span>{formatCurrency(invoice?.balance_due)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes */}
        {invoice?.notes && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <h3 className="font-semibold text-zinc-900 mb-2">Notes</h3>
              <p className="text-zinc-600 whitespace-pre-wrap">{invoice.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Company contact info */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <h3 className="font-semibold text-zinc-900 mb-3">Contact Us</h3>
            <div className="space-y-2 text-sm">
              {company.phone && (
                <a href={`tel:${company.phone}`} className="flex items-center gap-2 text-zinc-600 hover:text-emerald-600">
                  <Phone className="w-4 h-4" />
                  {company.phone}
                </a>
              )}
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-2 text-zinc-600 hover:text-emerald-600">
                  <Envelope className="w-4 h-4" />
                  {company.email}
                </a>
              )}
              {company.address && (
                <p className="flex items-center gap-2 text-zinc-600">
                  <MapPin className="w-4 h-4" />
                  {company.address}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Fixed bottom action bar - Pay Now button */}
      {canPay && !isPaid && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 p-4 safe-area-pb">
          <div className="max-w-3xl mx-auto">
            <Button
              onClick={handleOpenPay}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold"
              data-testid="pay-now-btn"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              Pay Now ({formatCurrency(invoice?.balance_due)})
            </Button>
          </div>
        </div>
      )}

      {/* Pay Dialog */}
      <Dialog open={showPayDialog} onOpenChange={(open) => {
        if (!processingPayment) {
          setShowPayDialog(open);
        }
      }}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => {
          if (processingPayment) e.preventDefault();
        }}>
          <DialogHeader>
            <DialogTitle>Pay Invoice</DialogTitle>
            <DialogDescription>
              Enter your card details to pay {formatCurrency(paymentConfig?.payment_amount)}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4 relative">
            {!finixReady && (
              <div className="flex items-center justify-center h-[150px] text-zinc-500 border border-zinc-200 rounded-lg">
                <SpinnerGap className="w-6 h-6 animate-spin mr-2" />
                Loading payment form...
              </div>
            )}
            
            <div 
              id="public-invoice-finix-form" 
              className={`min-h-[150px] ${!finixReady ? 'opacity-0 absolute' : ''}`}
              data-testid="finix-payment-form"
            />
            
            {processingPayment && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                <div className="flex flex-col items-center">
                  <SpinnerGap className="w-8 h-8 animate-spin text-emerald-600" />
                  <p className="text-zinc-600 mt-2">Processing payment...</p>
                </div>
              </div>
            )}
          </div>
          
          <div className="bg-zinc-50 rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="text-zinc-600">Amount to Pay</span>
              <span className="text-xl font-bold text-zinc-900">
                {formatCurrency(paymentConfig?.payment_amount)}
              </span>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowPayDialog(false)}
              disabled={processingPayment}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
